import { useState } from 'react';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9otse2qs38sj6jc7frg';

export default function ContactSection() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState('submitting');
    setFormError('');

    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormState('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      if (honeypotField) {
        formData.delete('phone_alt');
      }

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not json
      }

      const serverMsg = (parsed?.meta as { message?: string })?.message
        || (parsed?.meta as { detail?: string })?.detail
        || (parsed as { message?: string })?.message
        || responseText;

      if (response.ok && (parsed as { code?: string })?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else if (typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
        setFormError('提交失败，请稍后重试');
        setFormState('error');
      } else {
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormState('error');
    }
  };

  return (
    <section className="w-full max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
      <div className="text-center mb-8">
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50">
          <i className="ri-mail-send-line text-accent-400 mr-2"></i>联系我们
        </h2>
        <p className="text-sm text-foreground-400 mt-2">
          如有任何问题或建议，欢迎随时与我们联系
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        data-readdy-form
        className="space-y-4"
        noValidate
      >
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-200 mb-1.5">
            您的姓名 <span className="text-accent-400">*</span>
          </label>
          <input
            type="text"
            id="contact-name"
            name="name"
            required
            className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-300/60 text-sm text-foreground-100 placeholder-foreground-600 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/20 transition-all"
            placeholder="请输入您的姓名"
          />
        </div>

        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-200 mb-1.5">
            电子邮箱 <span className="text-accent-400">*</span>
          </label>
          <input
            type="email"
            id="contact-email"
            name="email"
            required
            className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-300/60 text-sm text-foreground-100 placeholder-foreground-600 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/20 transition-all"
            placeholder="请输入您的电子邮箱"
          />
        </div>

        <div>
          <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-200 mb-1.5">
            主题
          </label>
          <input
            type="text"
            id="contact-subject"
            name="subject"
            className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-300/60 text-sm text-foreground-100 placeholder-foreground-600 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/20 transition-all"
            placeholder="请输入主题（选填）"
          />
        </div>

        <div>
          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-200 mb-1.5">
            留言内容 <span className="text-accent-400">*</span>
          </label>
          <textarea
            id="contact-message"
            name="message"
            required
            maxLength={500}
            rows={5}
            className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-300/60 text-sm text-foreground-100 placeholder-foreground-600 focus:outline-none focus:border-accent-500/50 focus:ring-1 focus:ring-accent-500/20 transition-all resize-none"
            placeholder="请输入您的留言内容（最多500字）"
          ></textarea>
          <p className="text-xs text-foreground-500 mt-1">最多500字</p>
        </div>

        <input
          type="text"
          name="phone_alt"
          className="anti-spam-field"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        <button
          type="submit"
          disabled={formState === 'submitting'}
          className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 text-background-50 dark:text-foreground-950 font-medium rounded-lg text-sm transition-all whitespace-nowrap cursor-pointer"
        >
          {formState === 'submitting' ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>发送中...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-send-plane-line"></i>发送留言
            </span>
          )}
        </button>

        {formState === 'success' && (
          <div className="p-4 rounded-lg bg-accent-500/10 border border-accent-500/20 text-center">
            <p className="text-sm text-accent-400 font-medium">
              <i className="ri-check-line mr-1"></i>感谢您的留言！我们会尽快回复您。
            </p>
          </div>
        )}

        {formState === 'error' && formError && (
          <div className="p-4 rounded-lg bg-primary-500/10 border border-primary-500/20 text-center">
            <p className="text-sm text-primary-400 font-medium">
              <i className="ri-error-warning-line mr-1"></i>{formError}
            </p>
          </div>
        )}
      </form>
    </section>
  );
}